import { useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Accordion,
  useDisclosure,
  useOutsideClick,
  Box,
  Icon,
  InputRightElement,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import AccordionWrapper from '@/ui/accordion/accordianSearchWrapper';
import { X } from '@kula-ai/icons/outline/X';
import { colors } from '@/theme/foundations/colors';
import { FILTER_KINDS } from '../../utils/constants';
import { KText } from '@/ui-library/components';
import { LuInfo } from 'react-icons/lu';

export const FilterRowLabelField = ({
  options,
  value,
  onChange,
  customFilters,
  previousSelectedFilter,
  filterConfig = [],
  accordionTitle = 'Application Filters',
  showAccordion,
  showSearchInput,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { onClose, isOpen, onOpen } = useDisclosure();
  const selectedValue =
    Object.keys(value).length > 0 ? value.label || value.value : 'Select';
  const ref = useRef();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((key) => {
    // Check if the option is already selected in other filters
    const isAlreadySelected = previousSelectedFilter?.some(
      (filter) =>
        (filter.filter_by?.value === key || filter.filter_by?.label === key) &&
        filter.filter_by?.value !== value?.value
    );
    // For config filters, check the filterLabel
    const name = filterConfig[key]?.filterLabel?.toLowerCase();
    const matchesSearch = name?.includes(searchTerm?.toLowerCase());

    return matchesSearch && !isAlreadySelected;
  });

  const filteredCustomFields = customFilters?.filter((filter) => {
    // Basic filter conditions
    const isValidFilter =
      FILTER_KINDS?.includes(filter.kind) && !filter.deleted && !filter.multi;

    // Check if already selected in other filters (but not in current filter)
    const isAlreadySelected = previousSelectedFilter?.some(
      (f) =>
        f.filter_by?.value === filter.label && filter.label !== value?.value
    );

    // Check if matches search
    const label = filter?.label?.toLowerCase();
    const matchesSearch = label?.includes(searchTerm.toLowerCase());

    return isValidFilter && matchesSearch && !isAlreadySelected;
  });

  const handleCustomFilterChange = (customFilter) => {
    onChange({ value: customFilter?.id, label: customFilter?.label });
    onClose();
  };

  const onSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      if (dropdownRef.current) {
        dropdownRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  useOutsideClick({
    ref: ref,
    handler: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  const clearSearch = () => {
    setSearchTerm('');
  };

  // When component mounts, if no value is selected but options exist, select first available option
  useEffect(() => {
    if (
      !value?.value &&
      (filteredOptions.length > 0 || filteredCustomFields?.length > 0)
    ) {
      // Try config filters first
      if (filteredOptions.length > 0) {
        const firstOption = filteredOptions[0];
        onChange({
          value: firstOption,
          label: filterConfig[firstOption]?.filterLabel || firstOption,
        });
      }
      // If no config filters, try custom filters
      else if (filteredCustomFields?.length > 0) {
        const firstCustomFilter = filteredCustomFields[0];
        onChange({
          value: firstCustomFilter.label,
          label: firstCustomFilter.label,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const renderTrigger = () => {
    return (
      <Flex
        cursor="pointer"
        px={4}
        py="9px"
        borderRadius="xs"
        border="1px solid"
        borderColor={'border-color'}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          if (isOpen) {
            onClose();
          } else {
            onOpen();
            setTimeout(() => inputRef?.current?.focus(), 100);
          }
        }}
      >
        <KText isTruncated maxW="calc(100% - 1px)" enableTooltip>
          {selectedValue}
        </KText>

        <ChevronDownIcon
          width="16px"
          height="16px"
          color={colors['secondary-900']}
        />
      </Flex>
    );
  };

  const renderSearchInput = () => {
    return (
      <Flex width="350px" p="8px">
        <InputGroup alignItems="center">
          <InputLeftElement
            pointerEvents="none"
            children={
              <Icon as={SearchMd} color="secondary-800" width="18" h="100%" />
            }
          />
          <Input
            ref={inputRef}
            placeholder="Search"
            onChange={onSearch}
            value={searchTerm}
            _placeholder={{
              opacity: 1,
              fontSize: 'sm',
              fontWeight: 'medium',
              color: 'secondary-400',
            }}
            bg="secondary-50"
            borderColor="secondary-200"
            _hover={{
              bg: 'secondary-50',
              borderColor: 'secondary-200',
            }}
            _focus={{
              bg: 'secondary-50',
              boxShadow: 'none !important',
              borderColor: 'secondary-200 !important',
            }}
          />
          <InputRightElement>
            {searchTerm && (
              <Icon cursor="pointer" as={X} onClick={clearSearch} />
            )}
          </InputRightElement>
        </InputGroup>
      </Flex>
    );
  };

  const renderFilterOptions = () => {
    if (filteredOptions.length === 0 && filteredCustomFields?.length === 0) {
      return (
        <Box textAlign="center" py="20px">
          <KText>No matching Filters found.</KText>
        </Box>
      );
    }

    return (
      <Flex
        direction="column"
        overflowY="auto"
        ref={dropdownRef}
        maxHeight="290px"
      >
        {filteredOptions.length > 0 && (
          <>
            {/*
              Group and render filter options by their sections (e.g., 'Contact filters', 'Job filters')
              If a filter doesn't have a section specified, it goes into the 'Default' section
            */}
            {(() => {
              // Create an object that groups filters by their section
              // Example: { 'Contact filters': ['skills', 'location'], 'Job filters': ['job_title'] }
              const groupedOptions: Record<string, string[]> =
                filteredOptions.reduce(
                  (acc, key) => {
                    // Get section from filterConfig, fallback to 'Default' if not specified
                    const section = filterConfig[key]?.section || 'Default';
                    if (!acc[section]) {
                      acc[section] = [];
                    }
                    acc[section].push(key);
                    return acc;
                  },
                  {} as Record<string, string[]>
                );

              // Render a separate accordion for each section
              return Object.entries(groupedOptions).map(
                ([section, sectionOptions]) => {
                  if (!showAccordion) {
                    return (
                      <Box key={section}>
                        {sectionOptions.map((key) => {
                          const {
                            filterLabel: label,
                            isDisabled,
                            disabledFilterInfoMessage,
                          } = filterConfig[key];
                          const isSelected = previousSelectedFilter?.some(
                            (filter) => filter.filter_by?.value === key
                          );

                          return (
                            <Box
                              key={key}
                              onClick={() => {
                                if (!isDisabled) {
                                  onChange({ label, value: key });
                                  onClose();
                                }
                              }}
                              px={6}
                              py={3}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                              cursor={isDisabled ? 'not-allowed' : 'pointer'}
                              bg={
                                isSelected
                                  ? colors['hover-light']
                                  : 'transparent'
                              }
                              _hover={{
                                bg: isDisabled
                                  ? 'transparent'
                                  : colors['hover-light'],
                              }}
                              opacity={isDisabled ? 0.5 : 1}
                            >
                              <Flex
                                justify="flex-start"
                                align="center"
                                gap="12px"
                              >
                                <Flex align="center" gap={3}>
                                  <KText>{label}</KText>
                                </Flex>
                                {disabledFilterInfoMessage && (
                                  <Tooltip label={disabledFilterInfoMessage}>
                                    <Box>
                                      <Icon
                                        opacity={
                                          disabledFilterInfoMessage ? 0.5 : 1
                                        }
                                        as={LuInfo as any}
                                      />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Flex>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  return (
                    <AccordionWrapper
                      key={section}
                      // Use provided accordionTitle for 'Default' section, otherwise use section name
                      title={section === 'Default' ? accordionTitle : section}
                      count={sectionOptions?.length}
                      // Transform filter keys into option objects with label, value, and disabled state
                      options={sectionOptions?.map((key) => {
                        const {
                          filterLabel: label,
                          isDisabled: disabled,
                          disabledFilterInfoMessage,
                          labelTooltipText,
                        } = filterConfig[key];
                        return {
                          label,
                          value: key,
                          disabled: disabled,
                          disabledFilterInfoMessage,
                          labelTooltipText,
                        };
                      })}
                      onItemClick={(option) => {
                        onChange(option);
                        onClose();
                      }}
                      previousSelectedFilter={previousSelectedFilter}
                    />
                  );
                }
              );
            })()}
          </>
        )}
        {filteredCustomFields?.length > 0 && (
          <AccordionWrapper
            title="Custom filters"
            count={filteredCustomFields.length}
            options={filteredCustomFields || []}
            onItemClick={handleCustomFilterChange}
            isLoading={customFilters?.isLoading}
            isEmptyText="No matching Filters found."
            previousSelectedFilter={previousSelectedFilter}
          />
        )}
      </Flex>
    );
  };

  return (
    <Box ref={ref}>
      <Popover isOpen={isOpen}>
        <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
        <PopoverContent ml="75px" width="350px" borderRadius="sm">
          <PopoverBody p={0}>
            <Accordion allowMultiple={true} defaultIndex={[0, 1]}>
              {showSearchInput && renderSearchInput()}
              {renderFilterOptions()}
            </Accordion>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
